import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import {
  ChevronDown, ChevronRight, NotebookPen, AlertTriangle, Wrench,
  ClipboardList, Loader2,
} from "lucide-react";

/**
 * Intake Context Panel — surfaces the FOH Yestimate Intake Tech Intelligence
 * Pack on the technician's inspection screen.
 *
 * Behaviour:
 *   • Renders nothing when `intakeSessionId` is missing (job wasn't created
 *     from a Yestimate Intake session).
 *   • Collapsed by default — a single calm row with the journey label, the
 *     risk-flag count (if any), and an Expand affordance.
 *   • Expanded — shows risk flags first, operational preparation notes,
 *     structured findings, and any FOH notes.
 *
 * Data shape: GET /api/intake/sessions/{id} returns
 *   { journey_label, customer_name, vehicle_reg, status,
 *     operational_preparation: { tag_labels[], risk_labels[] },
 *     pack | pack_preview,
 *     foh_notes }
 *
 * Read-only — the technician cannot edit the intake.
 */
export default function IntakeContextPanel({ intakeSessionId, defaultExpanded = false, mode = "tech" }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Tracks whether we've already evaluated the auto-expand-on-risk rule
  // for this session — so a tech who manually collapses the panel after
  // reviewing the risks doesn't get it re-expanded on every re-render.
  const [autoExpandApplied, setAutoExpandApplied] = useState(false);

  useEffect(() => {
    if (!intakeSessionId) return;
    setLoading(true);
    axios
      .get(`${API}/intake/sessions/${intakeSessionId}`)
      .then((res) => setSession(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load intake context"))
      .finally(() => setLoading(false));
  }, [intakeSessionId]);

  // Auto-expand once when the loaded session carries safety-critical
  // risk flags — the tech needs to see them without an extra click.
  // Operational-prep tags alone aren't sufficient (they're mundane prep
  // items like "Adblue top-up" — not safety-critical).
  useEffect(() => {
    if (autoExpandApplied || !session) return;
    const risks = session?.operational_preparation?.risk_labels || [];
    if (risks.length > 0 && !defaultExpanded) {
      setExpanded(true);
    }
    setAutoExpandApplied(true);
  }, [session, defaultExpanded, autoExpandApplied]);

  if (!intakeSessionId) return null;

  if (loading && !session) {
    return (
      <div
        className="mb-4 rounded-lg border border-teal-200 bg-teal-50/30 px-4 py-3 text-[12.5px] text-teal-700 flex items-center gap-2"
        data-testid="intake-context-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading FOH intake context…
      </div>
    );
  }

  if (error || !session) {
    // Fail silent-ish — show a compact marker so we don't break the page
    return null;
  }

  const opPrep = session.operational_preparation || {};
  const risks = opPrep.risk_labels || [];
  const tags = opPrep.tag_labels || [];
  const pack = session.pack || session.pack_preview;
  const allSections = pack?.sections || [];
  const rawFohNotes = (session.foh_notes || "").trim();
  const journeyLabel = session.journey_label || "Other";

  // Role-aware filtering — testers only need the MOT-relevant slice. Strip
  // Service + Diagnostic blocks from both the pack sections and the
  // verbatim FOH notes blob (which the Phase 1 stage-3 capture stitches
  // together as `— MOT — / — Service — / — Diagnostic —`).
  const isTester = mode === "tester";
  const sections = isTester
    ? allSections.filter((s) => {
        const k = String(s.key || s.label || "").toLowerCase();
        return !k.includes("service") && !k.includes("diag");
      })
    : allSections;

  const fohNotes = isTester ? _filterFohNotesForTester(rawFohNotes) : rawFohNotes;

  return (
    <section
      className="mb-4 rounded-lg border border-teal-200 bg-gradient-to-br from-teal-50/60 to-white overflow-hidden"
      data-testid="intake-context-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-teal-50/40 transition-colors text-left"
        data-testid="intake-context-toggle"
      >
        <div className="w-7 h-7 rounded-full bg-teal-700 text-white flex items-center justify-center flex-shrink-0">
          <NotebookPen className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-teal-700 font-medium">
            FOH Intake Context · {journeyLabel}
          </p>
          <p className="text-[12.5px] text-slate-600 truncate">
            {pack?.headline ||
              `${session.customer_name || "Customer"} · ${session.vehicle_reg || "—"}`}
          </p>
        </div>
        {risks.length > 0 && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
            data-testid="intake-context-risk-count"
          >
            <AlertTriangle className="w-3 h-3" />
            {risks.length} risk{risks.length > 1 ? "s" : ""}
          </span>
        )}
        {tags.length > 0 && risks.length === 0 && (
          <span className="text-[11px] text-teal-700 bg-teal-100/70 border border-teal-200 px-2 py-0.5 rounded-full">
            {tags.length} note{tags.length > 1 ? "s" : ""}
          </span>
        )}
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-teal-700" />
        ) : (
          <ChevronRight className="w-4 h-4 text-teal-700" />
        )}
      </button>

      {expanded && (
        <div
          className="px-4 pb-4 pt-1 border-t border-teal-100 space-y-4 bg-white"
          data-testid="intake-context-body"
        >
          {/* Risk Flags — first, warm amber */}
          {risks.length > 0 && (
            <div data-testid="intake-context-risks">
              <div className="flex items-center gap-1.5 mb-1.5 text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-medium">
                  Risk flags
                </span>
              </div>
              <ul className="space-y-1">
                {risks.map((r) => (
                  <li
                    key={r}
                    className="text-[12.5px] text-amber-900 leading-snug pl-3 py-1 border-l-2 border-amber-400 bg-amber-50/70 rounded-r"
                  >
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Operational Preparation Notes */}
          {tags.length > 0 && (
            <div data-testid="intake-context-tags">
              <div className="flex items-center gap-1.5 mb-1.5 text-teal-800">
                <Wrench className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-medium">
                  Operational preparation
                </span>
              </div>
              <ul className="space-y-1 pl-1">
                {tags.map((t) => (
                  <li
                    key={t}
                    className="text-[12.5px] text-slate-700 leading-snug pl-3 border-l-2 border-teal-300"
                  >
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Structured findings (collapsed by category) */}
          {sections.length > 0 && !isTester && (
            <div data-testid="intake-context-sections">
              <div className="flex items-center gap-1.5 mb-1.5 text-slate-700">
                <ClipboardList className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-medium">
                  Customer conversation
                </span>
              </div>
              <div className="space-y-2.5">
                {sections.map((s) => (
                  <div key={s.key} className="border-l-2 border-slate-200 pl-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                      {s.label}
                    </p>
                    <ul className="text-[12.5px] text-slate-700 mt-0.5 space-y-0.5 leading-snug">
                      {(s.lines || []).map((ln, i) => (
                        <li key={i}>· {ln}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FOH free-text notes (verbatim) */}
          {fohNotes && (
            <div data-testid="intake-context-foh-notes">
              <div className="flex items-center gap-1.5 mb-1.5 text-slate-700">
                <NotebookPen className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-medium">
                  FOH notes
                </span>
              </div>

              <p className="text-[12.5px] text-slate-700 italic pl-3 border-l-2 border-slate-200 leading-snug whitespace-pre-line">
                {fohNotes}
              </p>
            </div>
          )}

          <p className="text-[10.5px] text-slate-400 italic pt-1">
            Captured by FOH during structured intake — read-only. The intake remains the source of truth for customer-reported context.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * Strip non-MOT blocks from the Phase 1 stitched foh_notes blob.
 *
 * The Start Intake Companion stitches stage-3 capture into a single blob
 * with section dividers:
 *
 *     Reason: <text>
 *
 *     — MOT —
 *     MOT due: 2026-06-14
 *     ...
 *
 *     — Service —
 *     Type: Full Service
 *     ...
 *
 *     — Diagnostic —
 *     Template: warning_light
 *     ...
 *
 * Testers only act on MOT-relevant context. Keep the opening `Reason:` line
 * plus the `— MOT —` block; drop everything from the next dashed header
 * onwards.
 */
function _filterFohNotesForTester(raw) {
  if (!raw) return "";
  const lines = String(raw).split("\n");
  const out = [];
  let inMot = true; // start "in" so we keep the opening Reason: lines
  for (const line of lines) {
    const stripped = line.trim();
    // Section header lines look like `— MOT —`, `— Service —`, `— Diagnostic —`
    const headerMatch = stripped.match(/^[—\-–]\s*([A-Za-z][A-Za-z /]*?)\s*[—\-–]$/);
    if (headerMatch) {
      const label = headerMatch[1].toLowerCase();
      inMot = label.includes("mot");
      if (inMot) out.push(line);
      continue;
    }
    if (inMot) out.push(line);
  }
  // Collapse any trailing whitespace
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
